import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Trash2, RefreshCw } from "lucide-react";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [denied, setDenied] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/overview").then(r => setOverview(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
    api.get("/admin/tenants").then(r => setTenants(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const retry = async (t) => {
    setBusy(t.id + "-retry");
    try {
      const r = await api.post(`/admin/tenants/${t.id}/retry-provision`);
      toast.success(`Receptionist set up${r.data?.number ? ` — number: ${r.data.number}` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Retry failed")); }
    finally { setBusy(""); }
  };

  const del = async () => {
    setBusy("del");
    try {
      const r = await api.delete(`/admin/tenants/${confirmDel.id}`);
      const notes = (r.data.provider_notes || []).join("; ");
      toast.success(`Deleted ${confirmDel.name}${notes ? ` — ${notes}` : ""}`);
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
    finally { setBusy(""); }
  };

  if (denied) {
    return <div className="p-8 text-slate-500" data-testid="admin-denied">Not authorized — this area is for the platform owner only.</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-950 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-white" /></div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Platform Admin</h1>
          <p className="text-slate-600 mt-1">Provider status, tenants, and technical controls. Owner only.</p>
        </div>
      </div>

      {overview && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-providers">
            <div className="font-semibold text-sm mb-3">Providers</div>
            {Object.entries(overview.providers).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm py-1">
                <span className="text-slate-500 capitalize">{k.replace("_", " ")}</span>
                <span data-testid={`provider-${k}`} className={v === true || (k === "stripe_mode" && v) ? "font-medium text-emerald-700" : "font-medium text-red-600"}>
                  {typeof v === "boolean" ? (v ? "connected" : "missing") : v}
                </span>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 break-all" data-testid="admin-vapi-server-url">
              Vapi server: <span className="font-mono">{overview.vapi_server_url}</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-counts">
            <div className="font-semibold text-sm mb-3">Totals</div>
            {[["Tenants", overview.tenants], ["User accounts", overview.users], ["Calls (excl. tests)", overview.calls], ["Test calls", overview.test_calls]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm py-1"><span className="text-slate-500">{l}</span><span className="font-medium">{v}</span></div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-cron">
            <div className="font-semibold text-sm mb-3">Scheduled jobs (recent)</div>
            {overview.recent_cron_activity.length === 0 && <div className="text-sm text-slate-400">No cron activity logged yet.</div>}
            {overview.recent_cron_activity.slice(0, 6).map((c) => (
              <div key={c.id || `${c.action}-${c.created_at}`} className="text-xs py-1 flex justify-between gap-2">
                <span className="text-slate-600">{c.action}</span>
                <span className="text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-x-auto max-w-6xl" data-testid="admin-tenants">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Tenant</th>
              <th className="text-left px-5 py-3">Subscription</th>
              <th className="text-left px-5 py-3">Assistant / numbers</th>
              <th className="text-left px-5 py-3">Data</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(tenants || []).map(t => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`tenant-row-${t.id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium">{t.name || "—"} {t.demo && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 ml-1">demo</span>}</div>
                  <div className="text-xs text-slate-500">{(t.users || []).join(", ") || "no login"}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{t.id}</div>
                </td>
                <td className="px-5 py-3 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${t.subscription_status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{t.subscription_status || "trial"}</span>
                  {t.plan && <span className="ml-1 text-slate-500">{t.plan}</span>}
                  {t.trial_ends_at && <div className="text-slate-400 mt-1">trial → {fmtDate(t.trial_ends_at)}</div>}
                </td>
                <td className="px-5 py-3 text-xs font-mono text-slate-600">
                  {t.vapi_assistant_id ? <div>{t.vapi_assistant_id.slice(0, 13)}…</div> : <div className="text-slate-400">no assistant</div>}
                  {(t.phone_numbers || []).map(n => <div key={n.number}>{n.number} ({n.provider || "twilio"}{n.voice_enabled ? ", voice" : ""})</div>)}
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">
                  {t.counts.appointments} appts · {t.counts.calls} calls · {t.counts.customers} customers
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {!t.vapi_assistant_id && (
                    <Button size="sm" variant="outline" className="rounded-lg mr-2" data-testid={`tenant-retry-${t.id}`}
                      disabled={busy} onClick={() => retry(t)}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />Set up
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" data-testid={`tenant-delete-${t.id}`}
                    disabled={busy} onClick={() => setConfirmDel(t)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tenants && tenants.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No tenants.</div>}
      </div>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="tenant-delete-dialog">
          <DialogHeader><DialogTitle>Delete {confirmDel?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently removes the business, its login accounts, appointments, customers, call logs, phone links, and test data,
            and detaches its Vapi assistant/number where possible. This cannot be undone.
          </p>
          {confirmDel?.subscription_status === "active" && confirmDel?.has_stripe_customer && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">This tenant has an active paid subscription — the server will refuse until it's cancelled in Stripe.</p>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setConfirmDel(null)}>Keep</Button>
            <Button data-testid="tenant-delete-confirm" onClick={del} disabled={busy === "del"} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700">
              {busy === "del" ? "Deleting…" : "Delete permanently"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
